import PropTypes from "prop-types";
import React, { useCallback } from "react";
import Icon from "../../core/Icon";
import TextLink from "../../core/TextLink";
import withTranslation from "../../core/withTranslation";
import ButtonV2 from "../../core/ButtonV2";
import Paragraph from "../../core/Paragraph";
import QuestionLabel from "../../core/QuestionLabel";
import FlexColumn from "../../core/FlexColumn";
import HorizontalDivider from "../../core/HorizontalDivider";
import FlexRow from "../../core/FlexRow";

function ContentFileRepository(props) {
  const [selectedAttachment, setSelectedAttachment] = React.useState(null);
  const childrenMap = new Map();
  const allowedExtensions = ["png", "pdf", "jpeg", "jpg", "gif", "mp3", "docx"];

  const setChildrenMap = useCallback((node, children) => {
    childrenMap.set(node.id, node);
    if (children == null) return;

    for (const child of children) {
      setChildrenMap(child, child.children);
    }
  }, []);

  const includeParentOnChildrenMap = useCallback(() => {
    for (const key of childrenMap.entries()) {
      const item = childrenMap.get(key);
      if (!item) {
        continue;
      }

      item.parent = childrenMap.get(item.parent);
      childrenMap.set(key, item);
    }
  }, []);

  props.root.children.forEach((val) => {
    if (val.parent) {
      childrenMap.set(val.parent.id, val.parent);
    }

    setChildrenMap(val, val.children);
    includeParentOnChildrenMap(val, val.children);
  });

  const getFileIconByExtension = useCallback((file) => {
    switch (file.fileUrl.split(".").pop()) {
      case "png":
      case "jpeg":
      case "jpg":
      case "gif":
        return "image";
      case "pdf":
        return "file-pdf";
      default:
        return "file";
    }
  }, []);

  const mountBreadcrumbIds = useCallback((node) => {
    let finalBreadcrumb = [];

    if (node.parent != null && childrenMap.get(node.parent) != null) {
      finalBreadcrumb = finalBreadcrumb.concat(mountBreadcrumbIds(childrenMap.get(node.parent)));
    }

    if (node != null) {
      finalBreadcrumb.push(node.id);
    }

    return finalBreadcrumb;
  }, []);

  const mountBreadcrumbData = useCallback((ids) => {
    const breadcrumb = [
      {
        attachment: props.root,
      },
    ];
    for (const id of ids) {
      breadcrumb.push({
        attachment: childrenMap?.get(id),
      });
    }

    return breadcrumb;
  }, []);

  function ContentFileRepositoryCategoryListItem(node) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          padding: "12px 0px",
        }}
        role="button"
        onClick={() => {
          setSelectedAttachment(node);
        }}
      >
        <div style={{ display: "flex", columnGap: 8, alignItems: "center" }}>
          {node.iconUrl ? (
            <img
              alt="node-icon"
              style={{ height: 32, width: "auto", objectFit: "contain" }}
              src={`${process.env.REACT_APP_FILES_URL}/${node.iconUrl}`}
            />
          ) : (
            <Icon icon="folder" />
          )}
          <div>
            <QuestionLabel text={node.title} fontSize={16} fontWeight="medium" />
            {node.description && <Paragraph>{node.description}</Paragraph>}
          </div>
        </div>
        <Icon icon="chevron-right" />
      </div>
    );
  }

  function ContentFileRepositoryFileListItem(file) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          padding: "12px 0px",
        }}
      >
        <div style={{ display: "flex", columnGap: 8, alignItems: "center" }}>
          <Icon icon={file.type === "LINK" ? "paperclip" : getFileIconByExtension(file)}></Icon>
          <div>
            <QuestionLabel text={file.title} fontSize={16} fontWeight="medium" />
            {file.description && <Paragraph>{file.description}</Paragraph>}
          </div>
        </div>
        <div style={{ display: "flex", columnGap: 8 }}>
          {allowedExtensions.includes(file.fileUrl.split(".").pop()) ? (
            <ButtonV2
              labelKey="preview"
              size="xs"
              buttonType="button"
              intent="primary"
              onClick={() => {
                props.handleOpenFilePreview(file);
              }}
            />
          ) : null}
          <ButtonV2
            labelKey={file.type === "LINK" ? "open" : "download"}
            size="xs"
            buttonType="button"
            intent="secondary"
            onClick={async () => {
              if (file.type === "LINK") {
                window.open(file.fileUrl, "__blank");
                return;
              }

              window.open(`${process.env.REACT_APP_FILES_URL}/${file.fileUrl}`, "__blank");
            }}
          />
        </div>
      </div>
    );
  }

  function ContentFileRepositoryList() {
    if (selectedAttachment) {
      return (
        <FlexColumn rowGap={4} padding={20} paddingTop={8}>
          {selectedAttachment?.children
            ? selectedAttachment.children.map((val, index) => {
                return (
                  <>
                    {!!index && <HorizontalDivider />}
                    {ContentFileRepositoryCategoryListItem(val)}
                  </>
                );
              })
            : null}
          {selectedAttachment?.files
            ? selectedAttachment.files.map((val, index) => {
                return (
                  <>
                    {!!index && <HorizontalDivider />}
                    {ContentFileRepositoryFileListItem(val)}
                  </>
                );
              })
            : null}
        </FlexColumn>
      );
    }
    return (
      <FlexColumn rowGap={4} padding={20} paddingTop={8}>
        {props.root.children
          ? props.root.children.map((val, index) => {
              return (
                <>
                  {!!index && <HorizontalDivider />}
                  {ContentFileRepositoryCategoryListItem(val)}
                </>
              );
            })
          : null}
        {props.root.files
          ? props.root.files.map((val, index) => {
              return (
                <>
                  {!!index && <HorizontalDivider />}
                  {ContentFileRepositoryFileListItem(val)}
                </>
              );
            })
          : null}
      </FlexColumn>
    );
  }

  return (
    <FlexColumn rowGap={8}>
      {selectedAttachment && (
        <FlexRow columnGap={4} alignItems="center" padding="12px 20px 8px 20px">
          {mountBreadcrumbData(mountBreadcrumbIds(selectedAttachment)).map((val, index) => {
            return (
              <>
                {!!index && <Icon icon="chevron-right" size="xs" />}
                {val.attachment?.id === selectedAttachment.id ? (
                  <Paragraph>{val.attachment?.title}</Paragraph>
                ) : (
                  <TextLink
                    label={val.attachment?.title}
                    onClick={() => {
                      setSelectedAttachment(val.attachment);
                    }}
                  />
                )}
              </>
            );
          })}
        </FlexRow>
      )}
      {selectedAttachment && <HorizontalDivider />}
      <ContentFileRepositoryList />
    </FlexColumn>
  );
}

const nodeType = PropTypes.shape({
  iconUrl: PropTypes.string,
  title: PropTypes.string,
  id: PropTypes.string,
  files: PropTypes.arrayOf({
    title: PropTypes.string,
    description: PropTypes.string,
    type: PropTypes.string,
    id: PropTypes.string,
  }),
  children: PropTypes.arrayOf(this),
  parent: PropTypes.shape(this),
});

ContentFileRepository.propTypes = {
  root: nodeType.isRequired,
  handleOpenFilePreview: PropTypes.func.isRequired,
};

export default withTranslation(ContentFileRepository);
